import { useState, useRef } from 'react';
import { Upload, X, FileSpreadsheet, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { creatorsService } from '@/services/creators';
import { useToast } from '@/hooks/use-toast';

interface CreatorUploadModalProps {
  isOpen: boolean;
  onOpenChange: (_open: boolean) => void;
  onSuccess?: () => void;
}

export default function CreatorUploadModal({
  isOpen,
  onOpenChange,
  onSuccess
}: CreatorUploadModalProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const csvFile = files.find(file =>
      file.type === 'text/csv' ||
      file.name.toLowerCase().endsWith('.csv') ||
      file.name.toLowerCase().endsWith('.xlsx') ||
      file.name.toLowerCase().endsWith('.xls')
    );

    if (csvFile) {
      setSelectedFile(csvFile);
    } else {
      toast({
        title: 'Arquivo inválido',
        description: 'Por favor, selecione um arquivo CSV ou Excel.',
        variant: 'error',
      });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const result = await creatorsService.upload(selectedFile);

      toast({
        title: 'Upload realizado com sucesso',
        description: `${result.imported} creators foram importados.`,
        variant: 'default',
      });

      onSuccess?.();
      onOpenChange(false);
      setSelectedFile(null);
    } catch (error) {
      console.error('Erro no upload:', error);
      toast({
        title: 'Erro no upload',
        description: 'Ocorreu um erro ao fazer upload do arquivo. Tente novamente.',
        variant: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      onOpenChange(false);
      setSelectedFile(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <div>
          <div
            className={`p-8 text-center transition-colors ${isDragOver ? 'bg-gray-50' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {selectedFile ? (
              <div className="space-y-4">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-600" />
                <div>
                  <p className="font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedFile(null)}
                  disabled={isUploading}
                >
                  <X className="w-4 h-4 mr-2" />
                  Remover
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 inline-flex border border-gray-200 rounded-md">
                  <Upload className="w-8 h-8 mx-auto text-black" />
                </div>
                <div>
                  <p
                    className="font-sans font-semibold text-xl leading-7 text-center"
                  >
                    Clique para fazer upload ou arraste seu arquivo
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Tipos de arquivo aceitos: .CSV
                  </p>
                </div>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="flex justify-center">
            {!selectedFile ? (
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-black text-white hover:bg-gray-900"
                disabled={isUploading}
              >
                Selecionar arquivo
              </Button>
            ) : (
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                className="bg-black text-white hover:bg-gray-900"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar arquivo'
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
